import { useDispatch, useSelector } from "react-redux";
import { getModule } from "store/userSlice";
import { useOutletContext } from "react-router-dom";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import { errorMessage, fileDownloadResponse, formatNumberTo } from "helpers/common";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { Button, Input } from 'components/ui';
import { getGeneralSettings } from "store/generalSettingsSlice";
import { fetchRegularEvaluationLevels, getRegularEvaluationLevels } from "store/balanceScorecardSlice";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { isDbModule } from "helpers/module";

const avatarColors = [
  { background: '#DCD7F5', color: '#3C3489' },
  { background: '#C0DDE6', color: '#0C447C' },
  { background: '#C8E0CC', color: '#27500A' },
  { background: '#F5C9D5', color: '#72243E' },
  { background: '#FAD9A0', color: '#633806' },
  { background: '#F0B7A0', color: '#4A1B0C' }
];

const settingSlugs = ['regelbeurteilung-regular', 'regelbeurteilung-leadership', 'anlassbeurteilung-regular', 'anlassbeurteilung-leadership'];

export default function AssessmentMatrix() {
  const dispatch = useDispatch();

  const { translate, editableTranslate } = useTranslation();

  const { overlay } = useOutletContext();

  const generalSettings = useSelector(getGeneralSettings);

  const regularEvaluationLevels = useSelector(getRegularEvaluationLevels);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingMoreEmployees, setLoadingMoreEmployees] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [objectives, setObjectives] = useState(null);

  const [search, setSearch] = useState('');

  const [employeeOptions, setEmployeeOptions] = useState([]);

  const [employeesPagination, setEmployeesPagination] = useState(null);

  const [activeObjectives, setActiveObjectives] = useState([]);

  const [activeEmployees, setActiveEmployees] = useState([]);

  const [bucketEmployees, setBucketEmployees] = useState([]);

  const [showAddDropdown, setShowAddDropdown] = useState(false);

  const [showExportDropdown, setShowExportDropdown] = useState(false);

  const [viewMode, setViewMode] = useState('trend');

  const searchRef = useRef(null);

  const timeoutRef = useRef(null);

  const employeeOptionsRef = useRef(null);

  const currentModule = useSelector(getModule);

  const isDbModuleActive = useMemo(() => {
    return isDbModule(currentModule);
  }, [currentModule]);

  const resultAsNumeric = useMemo(() => {
    if (!generalSettings) return false;

    // generalSettings is an array of object, search the key 'balanced_scorecard_result_format'
    const setting = generalSettings.find(s => s.key === 'balanced_scorecard_result_format');
    if (setting && setting.value === 'numeric') {
      return true;
    }

    return false;
  }, [generalSettings]);

  useEffect(() => {
    if (!isDbModuleActive) return;

    fetchStaticstic();
    fetchEmployees();

    if (!regularEvaluationLevels?.length) dispatch(fetchRegularEvaluationLevels());

    return () => clearTimeout(timeoutRef.current);
  }, [isDbModuleActive]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search) {
        fetchSearchEmployees(search);
      } else {
        setEmployeeOptions([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!showAddDropdown) {
      setSearch('');
    } else {
      checkNeedMoreEmployees();
    }
  }, [showAddDropdown]);

  useEffect(() => {
    checkNeedMoreEmployees();
  }, [bucketEmployees]);

  const fetchStaticstic = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data, status } = await api.get('/statistics/assessment_matrix');
      if (status === 200) {
        setObjectives(data.data);
        if (data.data) {
          setActiveObjectives(data.data.slice(0, 5));
        }
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch statistic'));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  const fetchEmployees = async () => {
    setError(null);
    setLoadingEmployees(true);
    try {
      const { data, status } = await api.get('/statistics/assessment_matrix/employees');
      if (status === 200) {
        const arr = data.data.map(item => {
          return {
            ...item,
            balanced_scorecards: {
              ...item.balanced_scorecards,
              data: item.balanced_scorecards.data.filter(bs => settingSlugs.includes(bs?.balanced_scorecard_setting?.slug) && bs?.evaluation_completed_at)
            }
          }
        });
        setActiveEmployees(arr.slice(0, 3));
        setBucketEmployees(arr.slice(3));

        setEmployeesPagination(data?.meta?.pagination || null);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch employees'));
      Sentry.captureException(err);
    }
    setLoadingEmployees(false);
  };

  const fetchMoreEmployees = async () => {
    if (loadingMoreEmployees) return;

    setError(null);
    setLoadingMoreEmployees(true);
    try {
      const { data, status } = await api.get('/statistics/assessment_matrix/employees?page=' + (+employeesPagination?.current_page + 1));
      if (status === 200) {
        const arr = data.data.map(item => {
          return {
            ...item,
            balanced_scorecards: {
              ...item.balanced_scorecards,
              data: item.balanced_scorecards.data.filter(bs => settingSlugs.includes(bs?.balanced_scorecard_setting?.slug) && bs?.evaluation_completed_at)
            }
          }
        });
        setBucketEmployees(prev => [...prev, ...arr]);

        setEmployeesPagination(data?.meta?.pagination || null);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch more employees'));
      Sentry.captureException(err);
    }
    setLoadingMoreEmployees(false);
  };

  const fetchSearchEmployees = async () => {
    setError(null);
    setLoadingSearch(true);
    try {
      const { data, status } = await api.get('/statistics/assessment_matrix/employees?search=' + search);
      if (status === 200) {
        const arr = data.data
          .filter(item => ![...activeEmployees].find(emp => emp.hash_id === item.hash_id && emp.balanced_scorecards.data.length === item.balanced_scorecards.data.length))
          .map(item => {
            return {
              ...item,
              balanced_scorecards: {
                ...item.balanced_scorecards,
                data: item.balanced_scorecards.data.filter(bs => settingSlugs.includes(bs?.balanced_scorecard_setting?.slug) && bs?.evaluation_completed_at)
              }
            }
          });

        setEmployeeOptions(arr);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch search employees'));
      Sentry.captureException(err);
    }
    setLoadingSearch(false);
  };

  const handleBlurAddEmployeeDropdown = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setShowAddDropdown(false);
    }
  };

  const handleBlurExportDropdown = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setShowExportDropdown(false);
    }
  };

  const handleClickObjective = (obj) => {
    const arr = structuredClone(activeObjectives);

    let foundIndex = null;

    for (let i = 0; i < arr.length; i++) {
      if (arr[i].objective === obj.objective) {
        foundIndex = i;
        break;
      }
    }

    if (foundIndex !== null) {
      arr.splice(foundIndex, 1);
    } else if (arr.length < 10) {
      arr.push(obj);
    }

    setActiveObjectives(arr);
  };

  const isActiveObjective = (obj) => {
    for (const item of activeObjectives) {
      if (item.objective === obj.objective) {
        return true;
      }
    }
    return false;
  };

  const getEvaluationLevel = (bs, obj) => {
    for (const savedObj of bs?.agreement_data?.systematicObjectives) {
      if (savedObj.name === obj.objective) {
        return savedObj;
      }
    }
    return null;
  };

  const getOverallResult = (bs) => {
    const overallResult = bs?.agreement_data?.overall_result;

    if (overallResult?.second_evaluator?.value) {
      return resultAsNumeric ? overallResult?.second_evaluator?.value : overallResult?.second_evaluator?.level;
    }

    return resultAsNumeric ? overallResult?.first_evaluator?.value : overallResult?.first_evaluator?.level;
  };

  const getLabeledPreviousEvaluationResult = (result) => {
    if (regularEvaluationLevels) {
      for (const lvl of regularEvaluationLevels) {
        if (lvl?.description !== 'sub' && +lvl.value === +result) {
          return resultAsNumeric ? lvl.value : lvl.level;
        }
      }
    }
  };

  const getEmployeeAvatarName = (employee) => {
    return employee?.first_name[0] + (employee?.last_name?.[0]);
  };

  const getPayGrade = (bs) => {
    return bs?.context_snapshot?.employee_period_data?.pay_grade;
  };

  const getPayGradeDiffer = (bs) => {
    if (bs?.context_snapshot?.employee_information) {
      for (const item of bs?.context_snapshot?.employee_information) {
        if (item?.key === 'job_differs_from_classification') {
          return item?.value;
        }
      }
    }
  };

  const convertDateFormat = (dateStr) => {
    // d.m.Y -> Y-m-d
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
      const [d, m, y] = dateStr.split('.');
      return `${y}-${m}-${d}`;
    }

    // Y-m-d -> d.m.Y
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-');
      return `${d}.${m}.${y}`;
    }

    return dateStr;
  };

  const getEvaluationPeriod = (bs) => {
    let fromDate = null;
    let toDate = null;

    if (bs?.context_snapshot?.employee_information) {
      for (const item of bs?.context_snapshot?.employee_information) {
        if (item?.key === 'evaluation_period_from') {
          fromDate = item?.value;
        } else if (item?.key === 'evaluation_period_to') {
          toDate = item?.value;
        }
      }
    }

    if (!fromDate && !toDate) {
      fromDate = bs?.context_snapshot?.period_context?.evaluation_period_start_date;
      if (fromDate) fromDate = convertDateFormat(fromDate);

      toDate = bs?.context_snapshot?.period_context?.evaluation_period_end_date;
      if (toDate) toDate = convertDateFormat(toDate);
    }

    if (fromDate && toDate) {
      return fromDate + ' - ' + toDate;
    }
  };

  const getEvaluationLevelClass = (level, obj) => {
    let lowest = null;
    let highest = null;

    for (const employee of activeEmployees) {
      for (const bs of employee?.balanced_scorecards?.data) {
        for (const savedObj of bs?.agreement_data?.systematicObjectives) {
          if (savedObj.name === obj.objective) {
            if (!lowest || parseFloat(formatNumberTo(lowest.value, '.')) < parseFloat(formatNumberTo(savedObj.value, '.'))) {
              lowest = savedObj;
            }

            if (!highest || parseFloat(formatNumberTo(highest.value, '.')) > parseFloat(formatNumberTo(savedObj.value, '.'))) {
              highest = savedObj;
            }
            break;
          }
        }
      }
    }

    if (parseFloat(formatNumberTo(level.value, '.')) === parseFloat(formatNumberTo(obj?.median?.value, '.'))) {
      return null;
    }

    if (level.value === lowest?.value) {
      return 'worst-td';
    } else if (level.value === highest?.value) {
      return 'best-td';
    }
  };

  const getEvaluationLevelDiff = (level, obj) => {
    if (!level) return null;

    let levelValue = parseFloat(formatNumberTo(level.value, '.'));
    let medianValue = parseFloat(formatNumberTo(obj.median?.value, '.'));
    let diff = levelValue - medianValue;

    if (diff > 0) {
      return '+' + Math.ceil(diff + 0.5);
    } else if (diff < 0) {
      return '-' + Math.ceil(Math.abs(diff) + 0.5);
    }
  };

  const getEvaluationLevelTooltip = (name, level, obj, diff) => {
    if (!level) return null;

    let medianText = translate('evaluation_statistics.assessment_matrix.same_as_median', 'entspricht Median');

    if (diff?.startsWith('+')) {
      medianText = diff + ' ' + translate('evaluation_statistics.assessment_matrix.worse_than_median', 'schlechter als Median');
    } else if (diff?.startsWith('-')) {
      medianText = diff + ' ' + translate('evaluation_statistics.assessment_matrix.better_than_median', 'besser als Median');
    }

    medianText += ` (${resultAsNumeric ? obj.median?.value : obj.median?.level})`;

    return `<div class="tooltip-evaluation">
    <div class="name-div">${name}</div>
    <div class="objective-div">${obj.objective}</div>
    <div class="level-div">${resultAsNumeric ? level.value : level.level}</div>
    <div class="median-text-div">${medianText}</div>
    </div>`;
  };

  const handleRemoveEmployee = (employee, bs) => {
    // allow to remove specific bs/evaluation instead of removing an employee entirely
    const newActiveEmployees = structuredClone(activeEmployees);
    for (let i = 0; i < newActiveEmployees.length; i++) {
      if (newActiveEmployees[i].hash_id === employee.hash_id) {
        newActiveEmployees[i].balanced_scorecards.data = newActiveEmployees[i].balanced_scorecards.data.filter(d => d.hash_id !== bs.hash_id);
        break;
      }
    }
    setActiveEmployees(newActiveEmployees.filter(e => e.balanced_scorecards.data.length > 0));

    let newEmployee = structuredClone(employee);

    let alreadyInBucket = false;
    const temp = structuredClone(bucketEmployees);
    for (let i = 0; i < temp.length; i++) {
      if (temp[i].hash_id === newEmployee.hash_id) {
        temp[i].balanced_scorecards.data.push(bs);
        alreadyInBucket = true;
        break;
      }
    }

    if (!alreadyInBucket) {
      newEmployee.balanced_scorecards.data = [bs];
      temp.push(newEmployee);
    }

    temp.sort((a, b) => {
      const strA = (a.name + a.employee_number).toLowerCase();
      const strB = (b.name + b.employee_number).toLowerCase();

      return strA.localeCompare(strB);
    });

    setBucketEmployees(temp);
  };

  const handleAddBackEmployee = (employee) => {
    const temp = structuredClone(activeEmployees);

    if (temp.length >= 10) {
      setShowAddDropdown(false);
      return;
    }

    let alreadyExist = false;
    for (let i = 0; i < temp.length; i++) {
      if (temp[i].hash_id === employee.hash_id) {
        for (const bs of employee.balanced_scorecards.data) {
          let bsExist = false;
          for (let j = 0; j < temp[i].balanced_scorecards.data.length; j++) {
            if (temp[i].balanced_scorecards.data[j].hash_id === bs.hash_id) {
              bsExist = true;
              break;
            }
          }
          if (!bsExist) temp[i].balanced_scorecards.data.push(bs);
        }

        alreadyExist = true;
        break;
      }
    }

    if (!alreadyExist) temp.push(employee);

    temp.sort((a, b) => {
      const strA = (a.name + a.employee_number).toLowerCase();
      const strB = (b.name + b.employee_number).toLowerCase();

      return strA.localeCompare(strB);
    });

    setActiveEmployees(temp);

    const newBucketEmployees = bucketEmployees.filter(p => p.hash_id !== employee.hash_id);
    setBucketEmployees(newBucketEmployees);

    const newEmployeeOptions = employeeOptions.filter(p => p.hash_id !== employee.hash_id);
    setEmployeeOptions(newEmployeeOptions);

    if (temp.length >= 10 || (search && newEmployeeOptions.length === 0) || (!search && newBucketEmployees.length === 0)) {
      setShowAddDropdown(false);
    } else if (!search) {
      checkNeedMoreEmployees();
    }

    searchRef?.current?.focus();
  };

  const handleScrollEmployees = (e) => {
    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      const el = e.target;

      const isBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 20;

      if (isBottom && +employeesPagination?.current_page < +employeesPagination?.total_pages) {
        fetchMoreEmployees();
      }
    }, 200);
  };

  const checkNeedMoreEmployees = () => {
    const el = employeeOptionsRef.current;

    if (!el) return;

    const noScrollbar = el.scrollHeight <= el.clientHeight;

    if (noScrollbar && +employeesPagination?.current_page < +employeesPagination?.total_pages) {
      fetchMoreEmployees();
    }
  };

  const handleExport = async (e = null) => {
    if (activeObjectives?.length <= 0 || activeEmployees?.length <= 0) return;

    const scorecards = [];
    for (const employee of activeEmployees) {
      if (employee?.balanced_scorecards?.data) {
        scorecards.push(...employee.balanced_scorecards.data.map(bs => bs.hash_id));
      }
    }
    if (scorecards.length <= 0) return;

    const formData = {
      key: 'assessment_matrix',
      parameters: {
        balanced_scorecard_hash_ids: scorecards,
        objectives: activeObjectives
      }
    };

    setError(null);
    setExportLoading(true);

    try {
      const response = await api.post('/exports/statistics', formData, {
        responseType: 'blob'
      });
      const { data } = response;

      if (data.type === 'application/json') {
        const json = JSON.parse(await data.text());

        if (json?.message && json?.message !== 'Success') {
          throw new Error(json.message);
        }
      } else {
        fileDownloadResponse(response, 'export.xlsx');
      }

      setExportLoading(false);
    } catch (err) {
      let errObj = { message: 'Something went wrong.' };

      try {
        errObj = JSON.parse(await err.response.data.text());
      } catch (err2) {
        if (err2.message) errObj = { message: err2.message };
      }

      setExportLoading(false);
      setError(errorMessage(errObj, 'generate Excel export'));
      Sentry.captureException(errObj);
    }
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <h4>{editableTranslate('global.menu.evaluation_statistics.assessment_matrix', 'Beurteilungsmatrix')}</h4>
          <p className="section-description">{editableTranslate('evaluation_statistics.assessment_matrix_description')}</p>

          {
            error && <div>
              <ErrorAlert error={error} onClose={() => setError(null)} />
            </div>
          }

          {
            !isDbModuleActive ? <>
              {overlay}
            </> : <div className="text-smaller mt-4">
              <div className="d-flex justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <span className="text-normal">{translate('evaluation_statistics.assessment_matrix.of_10_employees').replace('$X', activeEmployees.length)}</span>
                  <div className="position-relative ml-2" onBlur={handleBlurAddEmployeeDropdown}>
                    <Button
                      variant="secondary"
                      disabled={activeEmployees.length >= 10 || !objectives}
                      onClick={() => setShowAddDropdown(!showAddDropdown)} data-testid="add-button">+ {editableTranslate('evaluation_statistics.assessment_matrix.add', 'Hinzufügen')}</Button>
                    {
                      showAddDropdown && <div className="statistic-add-employee-dropdown">
                        <Input type="text" data-testid="employee-search-input" className="text-smaller" placeholder={translate('evaluation_statistics.assessment_matrix.employee_search', 'Beschäftigte/n suchen...')}
                          value={search} onChange={e => setSearch(e.target.value)} ref={searchRef} autoFocus />
                        <div className="max-h-200 mt-2" style={{ overflowY: 'auto' }} ref={employeeOptionsRef} onScroll={handleScrollEmployees}>
                          {
                            search ? <Fragment>
                              {
                                loadingSearch && <div className="text-center mt-1">
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                </div>
                              }
                              {
                                !loadingSearch && employeeOptions.map((employee, i) => <div key={i} data-testid={`employee-option-${employee.hash_id}`} className="statistic-add-employee-option" onClick={() => handleAddBackEmployee(employee)} tabIndex="0">
                                  <span className="statistic-add-employee-avatar" style={avatarColors[employee.name.length % 6]}>{getEmployeeAvatarName(employee)}</span>
                                  <span>{employee.name} - {employee.employee_number}</span>
                                </div>)
                              }
                              {
                                (!loadingSearch && employeeOptions.length === 0) && <div className="text-center mt-1">
                                  <span>{editableTranslate('global.no_options')}</span>
                                </div>
                              }
                            </Fragment> : <Fragment>
                              {
                                bucketEmployees.map((employee, i) => <div key={i} data-testid={`employee-option-${employee.hash_id}`} className="statistic-add-employee-option" onClick={() => handleAddBackEmployee(employee)} tabIndex="0">
                                  <span className="statistic-add-employee-avatar" style={avatarColors[employee.name.length % 6]}>{getEmployeeAvatarName(employee)}</span>
                                  <span>{employee.name} - {employee.employee_number}</span>
                                </div>)
                              }
                              {
                                (employeesPagination && +employeesPagination?.current_page < +employeesPagination?.total_pages) && <div className="text-center mt-1">
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                </div>
                              }
                            </Fragment>
                          }
                        </div>
                      </div>
                    }
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <span>{editableTranslate('evaluation_statistics.assessment_matrix.view', 'Ansicht')}:</span>
                  <div className="btn-group btn-group-statistic-mode ml-2">
                    <button type="button" onClick={() => setViewMode('matrix')} className={viewMode === 'matrix' ? 'active' : ''} data-testid="matrix-tab">{editableTranslate('evaluation_statistics.assessment_matrix.matrix', 'Matrix')}</button>
                    <button type="button" onClick={() => setViewMode('trend')} className={viewMode === 'trend' ? 'active' : ''} data-testid="trend-tab">{editableTranslate('evaluation_statistics.assessment_matrix.trend', 'Verlauf')}</button>
                  </div>
                  <div className="position-relative ml-4" onBlur={handleBlurExportDropdown}>
                    <Button variant="secondary" disabled={!objectives || activeObjectives?.length <= 0 || activeEmployees?.length === 0} loading={exportLoading} onClick={handleExport} data-testid="export-button">{editableTranslate('evaluation_statistics.assessment_matrix.export', 'Bericht/Export')}</Button>
                    {/* {
                      showExportDropdown && <div className="statistic-export-dropdown" tabIndex="0">
                        <div><i className="bi bi-filetype-pdf"></i> {editableTranslate('evaluation_statistics.assessment_matrix.pdf', 'PDF')}</div>
                        <div><i className="bi bi-file-earmark-excel"></i> {editableTranslate('evaluation_statistics.assessment_matrix.excel', 'Excel')}</div>
                      </div>
                    } */}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center flex-wrap gap-2" data-testid="objective-options">
                <span>{editableTranslate('evaluation_statistics.assessment_matrix.objective', 'Merkmale')}:</span>

                {
                  loading && <div className="text-center mt-1">
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  </div>
                }

                {
                  objectives && objectives.map((obj, i) => <div key={i}
                    onClick={() => handleClickObjective(obj)}
                    className={`statistic-chooseable-badge ${isActiveObjective(obj) ? 'active' : ''}`}>
                    <span className="">{obj.objective}</span>
                    <span className="ml-1">
                      {isActiveObjective(obj) ? <i className="bi bi-x-circle-fill"></i> : <i className="bi bi-plus"></i>}
                    </span>
                  </div>)
                }
              </div>

              <div className="mt-3">
                <div className="table-responsive">
                  <table className="table table-bordered statistic-assessment-matrix-table" data-testid="active-objectives-table">
                    <thead>
                      <tr>
                        <th className="text-uppercase font-weight-normal">{editableTranslate('evaluation_statistics.assessment_matrix.employees', 'Beschäftigte')}</th>
                        {
                          activeObjectives.map((obj, i) => <th key={i} className={`objective-th ${activeObjectives.length - 1 === i ? 'no-border-right' : ''}`} style={{ width: '120px' }}>
                            <div className="d-flex flex-column justify-content-end align-items-center">
                              <Button variant="ghost" size="inline" icon="bi-x" onClick={() => handleClickObjective(obj)} data-testid="remove-objective" />
                              <span className="text-rotate-left lh-1-2 line-clamp-2-default" data-tooltip-id="objective-tooltip" data-tooltip-content={obj.objective}>{obj.objective}</span>
                            </div>
                          </th>)
                        }
                        <th className="gap-cell"></th>
                        <th style={{ width: '120px' }}>
                          <div className="d-flex justify-content-center">
                            <span className="text-rotate-left">{editableTranslate('evaluation_statistics.assessment_matrix.overall', 'Gesamturteil')}</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="median-row">
                        <td className="text-uppercase text-right text-small">{editableTranslate('evaluation_statistics.assessment_matrix.median', 'Median')}</td>
                        {
                          activeObjectives.map((obj, i) => <td key={i} className="text-center">
                            <span className="text-default font-weight-bold">{resultAsNumeric ? obj.median?.value : obj.median?.level}</span>
                          </td>)
                        }
                        <td className="gap-cell"></td>
                        <td className="text-center">-</td>
                      </tr>

                      {
                        activeEmployees.map((employee, i) => <Fragment key={i}>
                          {
                            employee?.balanced_scorecards?.data?.map((bs, j) => <tr key={j} data-testid={`row-${employee?.employee_number}`}>
                              <td>
                                <div className="d-flex justify-content-between">
                                  <div className="d-flex">
                                    <span className="employee-avatar" style={avatarColors[employee.name.length % 6]}>{getEmployeeAvatarName(employee)}</span>
                                    <div className="d-flex flex-column gap-3px ml-2">
                                      <div className="d-flex align-items-baseline">
                                        <span className="name-span">{employee.name}</span>
                                        <span className="employee-number-span ml-2">{employee.employee_number}</span>
                                      </div>
                                      <div className="d-flex gap-6px">
                                        {(() => {
                                          const payGrade = getPayGrade(bs);
                                          const payGradeDiffer = getPayGradeDiffer(bs);

                                          return <>
                                            {payGrade && <span className="badge-span text-nowrap badge-pg">{payGrade}</span>}
                                            {payGradeDiffer && <span className="badge-span text-nowrap badge-pgd">{payGradeDiffer}</span>}
                                          </>;
                                        })()}
                                        <span className={`badge-span text-nowrap ${bs?.balanced_scorecard_setting?.slug?.includes('anlassbeurteilung') ? 'badge-ab' : 'badge-rb'}`}>{bs?.balanced_scorecard_setting?.name}</span>
                                      </div>
                                      <span className="period-span">{getEvaluationPeriod(bs)}</span>
                                    </div>
                                  </div>
                                  <Button variant="ghost" size="inline" icon="bi-x" onClick={() => handleRemoveEmployee(employee, bs)} data-testid={`remove-evaluation-${bs.hash_id}`} />
                                </div>
                              </td>
                              {
                                activeObjectives.map((obj, k) => {
                                  const level = getEvaluationLevel(bs, obj);
                                  const diff = getEvaluationLevelDiff(level, obj);

                                  return <td key={k} className={`text-center text-default cursor-pointer ${viewMode === 'matrix' ? 'v-middle' : 'trend-td'} ${(viewMode === 'matrix' && level) ? getEvaluationLevelClass(level, obj) : ''}`}
                                    data-tooltip-id="evaluation-level-tooltip"
                                    data-tooltip-html={getEvaluationLevelTooltip(employee.name, level, obj, diff)}>
                                    {
                                      (viewMode === 'matrix' && level) && <span className={`font-weight-600`}>{resultAsNumeric ? level?.value : level?.level}</span>
                                    }
                                    {
                                      (viewMode === 'trend' && level) && <span className={`evaluation-level-bar ${!diff ? 'zero' : ''} ${diff?.replace('-', 'up-').replace('+', 'down-')}`}></span>
                                    }
                                  </td>
                                })
                              }
                              <td className="gap-cell"></td>
                              <td className="text-center v-middle">
                                <span className="result-cell d-block font-weight-bold">{getOverallResult(bs)}</span>
                                {
                                  employee?.previous_evaluation_results?.regular_evaluation?.result && <span className="prev-cell">
                                    {editableTranslate('evaluation_statistics.assessment_matrix.prev', 'vorh.')} {getLabeledPreviousEvaluationResult(employee?.previous_evaluation_results?.regular_evaluation?.result)}
                                  </span>
                                }
                              </td>
                            </tr>)
                          }
                        </Fragment>)
                      }

                      {
                        loadingEmployees && <tr>
                          <td colSpan={activeObjectives.length + 3} className="text-center bg-white">
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          </td>
                        </tr>
                      }

                      {
                        (!loadingEmployees && activeEmployees.length === 0) && <tr>
                          <td colSpan={activeObjectives.length + 3} className="text-center bg-white" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>

                {
                  viewMode === 'trend' && <div className="d-flex align-items-center justify-content-center">
                    <span className="font-weight-bold">{editableTranslate('evaluation_statistics.assessment_matrix.left_line', '◀ Links der Linie:')}</span>
                    <span className="ml-1">{editableTranslate('evaluation_statistics.assessment_matrix.left_line_better_than_median', 'besser als Median (grün, je länger desto besser)')}</span>

                    <span className="font-weight-bold ml-3">{editableTranslate('evaluation_statistics.assessment_matrix.right_line', '▶ Rechts der Linie:')}</span>
                    <span className="ml-1">{editableTranslate('evaluation_statistics.assessment_matrix.right_line_better_than_median', 'schlechter als Median (orange/rot, je länger desto schlechter)')}</span>
                  </div>
                }

                <ReactTooltip id="objective-tooltip" place="bottom" />
                <ReactTooltip id="evaluation-level-tooltip" place="top" className="opacity-1" />
              </div>
            </div>
          }
        </div>
      </div>
    </>
  );
}
